import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Recommendation, TacticRecommendation } from "@/types/artie-score";

interface InviteCollaboratorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  task?: Recommendation | TacticRecommendation | null;
  onInvited?: (email: string) => void | Promise<void>;
}

export const InviteCollaboratorDialog: React.FC<InviteCollaboratorDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  task,
  onInvited,
}) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !projectId) return;

    setIsSubmitting(true);
    try {
      let taskName = "a task";
      let taskDescription = "You've been invited to collaborate.";
      let recId = undefined;

      if (task) {
        const isV2Tactic = "tactic_id" in task;
        taskName = isV2Tactic
          ? (task as TacticRecommendation).tactic_name
          : (task as Recommendation).what_to_improve;
        
        taskDescription = isV2Tactic
          ? (task as TacticRecommendation).tactic_description || (task as TacticRecommendation).gap_description
          : (task as Recommendation).how_to_improve || (task as Recommendation).why_it_matters;
        
        // Get the actual recommendation ID for status updates
        recId = task.id || (isV2Tactic ? (task as TacticRecommendation).tactic_id : taskName);
      }

      // Send the invitation
      // IMPORTANT: Pass taskName as task_id because the backend stores task names in the task_ids array
      await apiClient.addCollaborator(
        projectId,
        email,
        "edit", // Defaulting to edit for collaboration
        taskName, // Pass task name, not UUID - this gets stored in collaborator.task_ids
        taskName,
        taskDescription
      );

      // Update the task status to 'in_progress' when someone is invited
      if (task && recId) {
        const isV2Tactic = "tactic_id" in task;
        try {
          if (isV2Tactic) {
            await apiClient.updateTacticRecommendation(projectId, recId, {
              recommendation_id: recId,
              status: 'in_progress',
            });
          } else {
            await apiClient.updateRecommendation(projectId, {
              recommendation_id: recId,
              status: 'in_progress',
            });
          }
        } catch (error) {
          console.error("Failed to update task status:", error);
          // Don't block the invitation if status update fails
        }
      }

      // Invalidate queries to refresh the task list
      await queryClient.invalidateQueries({ queryKey: ['artieScoreStatus', projectId] });
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });

      toast({
        title: "Invitation sent!",
        description: `An email has been sent to ${email}`,
      });
      setEmail("");
      if (onInvited) {
        await onInvited(email);
      }
      onClose();
    } catch (error: any) {
      console.error("Failed to send invite:", error);
      toast({
        title: "Failed to send invitation",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Invite someone to help</DialogTitle>
          <DialogDescription>
            Send an email invitation to collaborate on this project.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleInvite}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            {task && (
              <div className="text-sm text-gray-500 italic px-4">
                They will receive an invitation specifically for the task:{" "}
                <span className="font-semibold text-gray-700">
                  {"tactic_id" in task
                    ? (task as TacticRecommendation).tactic_name
                    : (task as Recommendation).what_to_improve}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !email}>
              {isSubmitting ? "Sending..." : "Send Invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
